import isValidCalendarDateString from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/Helpers/isValidCalendarDateString';
import type FieldDraft from '@UI/Pages/Subjects/Shared/FormData/FieldDraft';
import isFieldDraftEmpty from '@UI/Pages/Subjects/Shared/FormData/constraint/isFieldDraftEmpty';
import type SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';

const MAX_TEXT_LENGTH = 255;
const MAX_LONG_TEXT_LENGTH = 5000;

type FieldShape = {
	readonly definition: SubjectFieldDefinitionView;
};

const isKnownOption = (definition: SubjectFieldDefinitionView, value: string): boolean =>
	definition.options.some((option) => option.value === value);

/**
 * A draft is valid when it is empty and the field is optional, or when its shape matches the nature
 * of the field. The bounds are the server's own. An unknown nature is never rejected here.
 */
const isFieldValueValid = (draft: FieldDraft, formData: FieldShape): boolean => {
	const { definition } = formData;

	if (isFieldDraftEmpty(draft)) {
		return !definition.required || definition.nature === 'boolean';
	}

	switch (definition.nature) {
		case 'text':
			return typeof draft === 'string' && draft.trim().length <= MAX_TEXT_LENGTH;
		case 'long_text':
			return typeof draft === 'string' && draft.trim().length <= MAX_LONG_TEXT_LENGTH;
		case 'date':
			return typeof draft === 'string' && isValidCalendarDateString(draft);
		case 'number':
			return typeof draft === 'number' && Number.isFinite(draft);
		case 'boolean':
			return typeof draft === 'boolean';
		case 'choice':
			return typeof draft === 'string' && isKnownOption(definition, draft);
		case 'multiple_choice':
			return (
				Array.isArray(draft) &&
				new Set(draft).size === draft.length &&
				draft.every((value) => isKnownOption(definition, value))
			);
		default:
			return true;
	}
};

export default isFieldValueValid;
